import { useTranslation } from 'react-i18next';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

interface TableSkeletonProps {
  /**
   * How many columns to mirror. Match the real table's header count so the
   * placeholder lines up with the data that lands. Default 5.
   */
  columns?: number;
  /** How many placeholder rows to draw. Default 6. */
  rows?: number;
  /**
   * Drop the outer card chrome (border + radius + background). Use when the
   * caller already wraps the table in its own card so we don't double up.
   */
  bare?: boolean;
  className?: string;
}

// Per-column body-cell widths, cycled by column index so each column reads
// as a distinct kind of content (wide name, medium text, short status …)
// rather than a wall of identical bars. Deterministic — no per-render jitter.
const CELL_WIDTHS = ['w-40', 'w-28', 'w-24', 'w-32', 'w-20', 'w-28'];

/**
 * TableSkeleton — a polished, self-contained loading placeholder for any
 * table page. Renders a header bar plus shimmering rows inside the same
 * rounded card the real tables live in, so the page keeps its shape while
 * data loads instead of flashing a bare "Loading…" line.
 *
 * Drop-in replacement for the old `<p>{t('common.loading')}</p>` branch:
 *
 *   if (query.isLoading) return <TableSkeleton columns={6} />;
 *
 * The last column is drawn as a small pill to stand in for the row-actions
 * cell most tables carry. The whole block is a single `role="status"`
 * region labelled "loading" — the individual bars are decorative.
 */
export function TableSkeleton({
  columns = 5,
  rows = 6,
  bare = false,
  className,
}: TableSkeletonProps) {
  const { t } = useTranslation('common');
  const cols = Array.from({ length: Math.max(columns, 1) });

  // Wide first column (names/titles), even middle columns, a narrow trailing
  // column for the actions cell. Mirrors the shape of nearly every table in
  // the app. Computed template is the same dynamic-grid pattern MemberList
  // uses, so it stays an accepted exception to the no-inline-style rule.
  const template =
    columns <= 1
      ? '1fr'
      : `1.6fr ${Array(Math.max(columns - 2, 0))
          .fill('1fr')
          .join(' ')} 2.75rem`.trim();

  return (
    <div
      role="status"
      aria-busy="true"
      aria-label={t('common.loading')}
      className={cn(
        !bare && 'overflow-hidden rounded-2xl border border-border bg-background',
        className,
      )}
    >
      <div
        className="grid items-center gap-4 border-b border-border bg-muted/40 px-4 py-3"
        style={{ gridTemplateColumns: template }}
      >
        {cols.map((_, c) => (
          <Skeleton
            key={c}
            className={cn(
              'h-3 rounded-pill',
              c === columns - 1 && columns > 1 ? 'ml-auto w-5' : 'w-16',
            )}
          />
        ))}
      </div>
      <div className="divide-y divide-border">
        {Array.from({ length: rows }).map((_, r) => (
          <div
            key={r}
            className="grid items-center gap-4 px-4 py-[1.15rem]"
            style={{ gridTemplateColumns: template }}
          >
            {cols.map((_, c) => {
              const isActionCol = c === columns - 1 && columns > 1;
              return isActionCol ? (
                <Skeleton key={c} className="ml-auto h-7 w-7 rounded-pill" />
              ) : (
                <Skeleton
                  key={c}
                  className={cn('h-4', CELL_WIDTHS[c % CELL_WIDTHS.length])}
                />
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
